import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { ErrorBoundary } from '../components/ErrorBoundary/ErrorBoundary';
import { useTenantScreeningFlow } from '../hooks/useTenantScreeningFlow';

import {
  AddressStep,
  RoleSelectionStep,
  OwnerInfoStep,
  AgreementStep,
  PackageSelectionStep,
  PayeeStep,
  SuccessStep,
} from './add-tenant-screening-steps/index';

export const AddTenantScreeningPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentStep,
    formData,
    isLoading,
    goToNextStep,
    goToPrevStep,
    updateAddress,
    updateUserRole,
    updateOwnerInfo,
    updateAgreement,
    updateSelectedPackage,
    updatePayeeConfig,
    submitForm,
    resetForm,
  } = useTenantScreeningFlow();

  const handleHomeClick = () => {
    // Navigate to the home page
    navigate('/');
  };

  const handleExitClick = () => {
    // Reset form and navigate to the home page
    resetForm();
    navigate('/');
  };

  const handleEmailSend = async (_emails: string[]) => {
    // In real implementation, this would send emails via API
    // console.log('Sending emails to:', emails);
    // Mock delay
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleCopyLink = () => {
    // console.log('Link copied');
  };

  const handleGenerateQR = () => {
    // console.log('QR code generated');
  };

  const handleSocialShare = (_platform: string) => {
    // console.log('Sharing on:', platform);
  };

  const handleGetMoreApplicants = () => {
    // Navigate to listing service or upsell page
    // console.log('Get more applicants clicked');
  };

  const handleReturnToDashboard = () => {
    // Navigate back to main dashboard
    // console.log('Return to dashboard clicked');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <AddressStep
            address={formData.address || {}}
            onAddressChange={updateAddress}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            isLoading={isLoading}
          />
        );

      case 2:
        return (
          <RoleSelectionStep
            selectedRole={formData.userRole}
            onRoleChange={updateUserRole}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            onHomeClick={handleHomeClick}
            onExitClick={handleExitClick}
            isLoading={isLoading}
          />
        );

      case 3:
        // Don't render if userRole is not set (should not happen in normal flow)
        if (!formData.userRole) {
          return null;
        }
        return (
          <OwnerInfoStep
            ownerInfo={formData.ownerInfo || {}}
            userRole={formData.userRole}
            onOwnerInfoChange={updateOwnerInfo}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            onHomeClick={handleHomeClick}
            onExitClick={handleExitClick}
            isLoading={isLoading}
          />
        );

      case 4:
        return (
          <AgreementStep
            agreement={formData.agreement || {}}
            onAgreementChange={updateAgreement}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            onHomeClick={handleHomeClick}
            onExitClick={handleExitClick}
            isLoading={isLoading}
          />
        );

      case 5:
        return (
          <PackageSelectionStep
            selectedPackage={formData.selectedPackage}
            onPackageChange={updateSelectedPackage}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            onHomeClick={handleHomeClick}
            onExitClick={handleExitClick}
            isLoading={isLoading}
          />
        );

      case 6:
        return (
          <PayeeStep
            payeeConfig={formData.payeeConfig || {}}
            selectedPackage={formData.selectedPackage}
            onPayeeConfigChange={updatePayeeConfig}
            onNext={async () => {
              try {
                await submitForm();
                goToNextStep();
              } catch (error) {
                console.error('Form submission failed:', error);
              }
            }}
            onBack={goToPrevStep}
            onHomeClick={handleHomeClick}
            onExitClick={handleExitClick}
            isLoading={isLoading}
          />
        );

      case 7:
        return (
          <SuccessStep
            formData={formData}
            onEmailSend={handleEmailSend}
            onCopyLink={handleCopyLink}
            onGenerateQR={handleGenerateQR}
            onSocialShare={handleSocialShare}
            onGetMoreApplicants={handleGetMoreApplicants}
            onReturnToDashboard={handleReturnToDashboard}
            onHomeClick={handleHomeClick}
            isLoading={isLoading}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ErrorBoundary>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {renderCurrentStep()}
      </Box>
    </ErrorBoundary>
  );
};

export default AddTenantScreeningPage;
